import { z } from 'zod';
import type { TransactionType } from '../types';
import { centsSchema, isoDateSchema, positiveCentsSchema, uuidSchema } from './common';

const commonFields = {
  occurredOn: isoDateSchema,
  label: z.string().trim().max(120).optional(),
  note: z.string().trim().max(500).nullable().optional(),
  /** Par défaut, l'appareil qui saisit ; modifiable quand on emprunte un téléphone. */
  paidBy: z.string().trim().max(60).nullable().optional(),
};

/**
 * L'API reçoit des montants POSITIFS — ce que l'utilisateur tape — et le
 * serveur applique le signe selon le type. Le stockage, lui, est signé du point
 * de vue du compte.
 */
export const transactionCreateSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('expense'),
    accountId: uuidSchema,
    amountCents: positiveCentsSchema,
    categoryId: uuidSchema.nullable().optional(),
    /**
     * Pioche : la dépense est financée par un pôle. Le serveur engendre le
     * virement qui la couvre, et les deux écritures sont neutres sur le reste
     * à vivre du mois.
     */
    fundedByGoalId: uuidSchema.nullable().optional(),
    ...commonFields,
  }),
  z.object({
    type: z.literal('income'),
    accountId: uuidSchema,
    amountCents: positiveCentsSchema,
    categoryId: uuidSchema.nullable().optional(),
    ...commonFields,
  }),
  z.object({
    type: z.literal('transfer'),
    /** Compte source : c'est lui qui est débité. */
    accountId: uuidSchema,
    counterpartAccountId: uuidSchema,
    amountCents: positiveCentsSchema,
    /** Pôle alimenté : l'argent doit atterrir sur le compte qui le porte. */
    savingsGoalId: uuidSchema.nullable().optional(),
    ...commonFields,
  }),
  z.object({
    /** Recalage manuel : le seul cas où le signe est fourni tel quel. */
    type: z.literal('adjustment'),
    accountId: uuidSchema,
    amountCents: centsSchema.refine((value) => value !== 0, 'Un recalage nul n’a pas de sens'),
    ...commonFields,
  }),
]);

export type TransactionCreateInput = z.infer<typeof transactionCreateSchema>;

/**
 * Le type d'une écriture ne se change pas : on supprime et on ressaisit.
 * Modifier un `expense` en `transfer` demanderait de réécrire ses deux côtés.
 */
export const transactionUpdateSchema = z.object({
  /**
   * Le serveur réapplique le signe selon le type existant : envoyer 1250 ou
   * -1250 pour une dépense donne le même résultat. Seul un recalage conserve
   * le signe fourni.
   */
  amountCents: centsSchema.refine((value) => value !== 0, 'Un montant nul n’a pas de sens').optional(),
  occurredOn: isoDateSchema.optional(),
  label: z.string().trim().max(120).optional(),
  categoryId: uuidSchema.nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
  paidBy: z.string().trim().max(60).nullable().optional(),
});

export type TransactionUpdateInput = z.infer<typeof transactionUpdateSchema>;

/**
 * Ventilation d'une dépense entre plusieurs catégories.
 *
 * Le serveur crée N écritures partageant un identifiant de groupe. La somme
 * des parts doit égaler le total : le montant vient d'un ticket, il est fixe.
 */
export const transactionSplitSchema = z.object({
  accountId: uuidSchema,
  totalCents: positiveCentsSchema,
  occurredOn: isoDateSchema,
  label: z.string().trim().max(120).optional(),
  paidBy: z.string().trim().max(60).nullable().optional(),
  parts: z
    .array(
      z.object({
        categoryId: uuidSchema,
        amountCents: positiveCentsSchema,
      }),
    )
    .min(2, 'Une ventilation demande au moins deux catégories')
    .max(12, 'Douze parts suffisent largement'),
});

export type TransactionSplitInput = z.infer<typeof transactionSplitSchema>;

export const transactionQuerySchema = z.object({
  accountId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export type TransactionQuery = z.infer<typeof transactionQuerySchema>;

export interface TransactionResponse {
  id: string;
  type: TransactionType;
  /** Signé, du point de vue de `accountId`. */
  amountCents: number;
  accountId: string;
  counterpartAccountId: string | null;
  occurredOn: string;
  label: string;
  categoryId: string | null;
  savingsGoalId: string | null;
  fundedByGoalId: string | null;
  recurringItemId: string | null;
  /** Non nul : cette écriture est une part d'un achat ventilé. */
  splitGroupId: string | null;
  deviceId: string | null;
  paidBy: string | null;
  note: string | null;
}

export interface TransactionListResponse {
  items: TransactionResponse[];
  total: number;
}
